import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import Pagination from "../../components/Pagination";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import moment from "moment";
import "moment/locale/th";
import { formatDate as formatDateUtil } from "../../../utils/DateUtils";
moment.locale("th");

// คำนวณ Due Date จาก Due_Date หรือ CT_Due_Date + 30 วัน
const getDueDate = (inv) => {
  if (inv.Due_Date) {
    const d = new Date(inv.Due_Date);
    return isNaN(d.getTime()) ? null : d;
  }
  if (inv.CT_Due_Date) {
    const d = new Date(inv.CT_Due_Date);
    d.setDate(d.getDate() + 30);
    return d;
  }
  return null;
};

const formatIsoToBE = (isoDate) => {
  if (!isoDate) return "-";
  const m = moment(isoDate, "YYYY-MM-DD", true);
  if (!m.isValid()) return "-";
  return `${m.format("DD/MM")}/${m.year() + 543}`;
};

export default function JobFileInvoiceRecordListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const actionRef = useRef(null);
  const tableScrollRef = useRef(null);
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  const [rightOffset, setRightOffset] = useState(0);
  const [viewMode, setViewMode] = useState("contract"); // "contract" | "monthly"
  const [currentPageMonthly, setCurrentPageMonthly] = useState(1);
  const [itemsPerPageMonthly, setItemsPerPageMonthly] = useState(100);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedActionKey, setSelectedActionKey] = useState("");

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    if (actionRef.current) {
      setRightOffset(actionRef.current.getBoundingClientRect().width);
    }
  }, []);

  useEffect(() => {
    setCurrentPageMonthly(1);
  }, [startDate, endDate, selectedYear, selectedStatus]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("tab");
    const searchValue = params.get("search") || "";
    const page = Number.parseInt(params.get("page") || "1", 10);
    const pageSize = Number.parseInt(params.get("pageSize") || "100", 10);
    const pageMonthly = Number.parseInt(params.get("pageMonthly") || "1", 10);
    const pageSizeMonthly = Number.parseInt(params.get("pageSizeMonthly") || "100", 10);
    const start = params.get("startDate") || "";
    const end = params.get("endDate") || "";
    const year = params.get("year") || "";
    const status = params.get("status") || "";
    const focus = params.get("focus") || "";

    setViewMode(mode === "monthly" ? "monthly" : "contract");
    setSearch(searchValue);
    setCurrentPage(Number.isFinite(page) && page > 0 ? page : 1);
    setItemsPerPage(Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 100);
    setCurrentPageMonthly(Number.isFinite(pageMonthly) && pageMonthly > 0 ? pageMonthly : 1);
    setItemsPerPageMonthly(Number.isFinite(pageSizeMonthly) && pageSizeMonthly > 0 ? pageSizeMonthly : 100);
    setStartDate(start);
    setEndDate(end);
    setSelectedYear(year);
    setSelectedStatus(status);
    setSelectedActionKey(focus);
  }, [location.search]);

  const buildInvoiceRecordBackLink = (focusKey = "") => {
    const params = new URLSearchParams();
    params.set("tab", viewMode);
    if (viewMode === "contract") {
      if (search) params.set("search", search);
      params.set("page", String(currentPage || 1));
      params.set("pageSize", String(itemsPerPage || 100));
    } else {
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (selectedYear) params.set("year", selectedYear);
      if (selectedStatus) params.set("status", selectedStatus);
      params.set("pageMonthly", String(currentPageMonthly || 1));
      params.set("pageSizeMonthly", String(itemsPerPageMonthly || 100));
    }
    if (focusKey) params.set("focus", focusKey);
    const qs = params.toString();
    return `${location.pathname}${qs ? `?${qs}` : ""}`;
  };

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const compId = localStorage.getItem("company");
      const response = await api.get("/get-contracts", {
        params: compId ? { comp_id: compId } : {}
      });

      if (response.data?.status === "OK") {
        const contractsList = response.data.result || [];
        
        // ดึงข้อมูล invoice สำหรับแต่ละสัญญา
        const contractsWithInvoices = await Promise.all(
          contractsList.map(async (contract) => {
            try {
              const invoiceRes = await api.get(`/get-contract-invoices/${contract.Doc_Id}`);
              let hasUnpaidInvoices = false;
              let unpaidCount = 0;
              let totalUnpaidAmount = 0;
              
              if (invoiceRes.data.status === "OK") {
                const invoices = invoiceRes.data.result || [];
                const unpaid = invoices.filter(inv => {
                  const paid = inv.Paid_Amount || 0;
                  const toPay = inv.CT_Due_Amount || 0;
                  return paid < toPay;
                });
                hasUnpaidInvoices = unpaid.length > 0;
                unpaidCount = unpaid.length;
                totalUnpaidAmount = unpaid.reduce((sum, inv) => {
                  const paid = inv.Paid_Amount || 0;
                  const toPay = inv.CT_Due_Amount || 0;
                  return sum + (toPay - paid);
                }, 0);
                // เก็บรายละเอียด invoice ทั้งหมดสำหรับมุมมองรายเดือน
                const allInvoices = invoices.map(inv => {
                  const paid = inv.Paid_Amount || 0;
                  const toPay = inv.CT_Due_Amount || 0;
                  const remaining = toPay - paid;
                  const dueDate = getDueDate(inv);
                  const now = new Date();
                  now.setHours(0, 0, 0, 0);
                  const dueDateOnly = dueDate ? new Date(dueDate) : null;
                  if (dueDateOnly) dueDateOnly.setHours(0, 0, 0, 0);
                  const isOverdue = dueDateOnly && dueDateOnly < now && remaining > 0;
                  const paymentStatus =
                    paid >= toPay && toPay > 0
                      ? "paid"
                      : paid > 0
                        ? "partial"
                        : isOverdue
                          ? "overdue"
                          : "pending";

                  return {
                    ...inv,
                    paidAmount: paid,
                    toPayAmount: toPay,
                    remainingAmount: remaining,
                    dueDate,
                    monthKey: dueDate ? moment(dueDate).format("YYYY-MM") : null,
                    paymentStatus,
                    Review_Status: inv.Review_Status ?? 0,
                  };
                });
                return {
                  ...contract,
                  hasUnpaidInvoices,
                  unpaidCount,
                  totalUnpaidAmount,
                  allInvoices: allInvoices.filter(i => i.monthKey),
                };
              }
              
              return {
                ...contract,
                hasUnpaidInvoices,
                unpaidCount,
                totalUnpaidAmount,
                allInvoices: [],
              };
            } catch (err) {
              return {
                ...contract,
                hasUnpaidInvoices: false,
                unpaidCount: 0,
                totalUnpaidAmount: 0,
                allInvoices: [],
              };
            }
          })
        );

        // กรองเฉพาะสัญญาที่มี invoice ที่ยังไม่ชำระครบ
        const filtered = contractsWithInvoices.filter(c => c.hasUnpaidInvoices);
        setContracts(filtered);
      } else {
        setContracts([]);
      }
    } catch (error) {
      console.error("Error fetching contracts:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถดึงข้อมูลได้",
      });
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search
  const filtered = contracts.filter((contract) => {
    const key = search.toLowerCase();
    const contractNo = (contract.Doc_No || "").toLowerCase();
    const customerName = (contract.DisplayName || contract.AR_Name || "").toLowerCase();
    return contractNo.includes(key) || customerName.includes(key);
  });

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting
  const sorted = [...filtered].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Special handling for dates
    if (sortConfig.key === 'Created_Date') {
      const aDate = aValue ? new Date(aValue) : new Date(0);
      const bDate = bValue ? new Date(bValue) : new Date(0);
      if (sortConfig.direction === 'asc') {
        return aDate - bDate;
      } else {
        return bDate - aDate;
      }
    }

    // Special handling for numbers
    if (sortConfig.key === 'unpaidCount' || sortConfig.key === 'totalUnpaidAmount') {
      const aNum = Number(aValue) || 0;
      const bNum = Number(bValue) || 0;
      if (sortConfig.direction === 'asc') {
        return aNum - bNum;
      } else {
        return bNum - aNum;
      }
    }

    // Convert to string for comparison
    if (typeof aValue !== 'string') aValue = String(aValue);
    if (typeof bValue !== 'string') bValue = String(bValue);

    if (sortConfig.direction === 'asc') {
      return aValue.localeCompare(bValue, 'th', { numeric: true });
    } else {
      return bValue.localeCompare(aValue, 'th', { numeric: true });
    }
  });

  const totalItems = sorted.length;
  const from = (currentPage - 1) * itemsPerPage;
  const paginated = sorted.slice(from, from + itemsPerPage);

  // รายการตามเดือน: รวม unpaid invoices จากทุกสัญญา แยกตามเดือนครบกำหนด
  const monthlyItems = contracts.flatMap((c) =>
    (c.allInvoices || []).map((inv) => ({
      ...inv,
      Doc_Id: c.Doc_Id,
      Doc_No: c.Doc_No,
      DisplayName: c.DisplayName || `${c.AR_Name || ""} ${c.AR_LastName || ""}`.trim(),
    }))
  );
  const monthlyByKey = monthlyItems.reduce((acc, item) => {
    const key = item.monthKey;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  const yearOptions = Array.from(
    new Set(
      monthlyItems
        .map((i) => (i.dueDate ? new Date(i.dueDate).getFullYear() : null))
        .filter(Boolean)
    )
  ).sort((a, b) => b - a);
  const currentYear = new Date().getFullYear();
  if (!yearOptions.includes(currentYear)) yearOptions.unshift(currentYear);

  const filteredMonthlyItems = monthlyItems.filter((item) => {
    const d = item.dueDate ? new Date(item.dueDate) : null;
    if (!d || isNaN(d.getTime())) return false;

    if (selectedYear) {
      const y = parseInt(selectedYear, 10);
      if (Number.isFinite(y) && d.getFullYear() !== y) return false;
    }
    if (startDate) {
      const s = new Date(startDate);
      s.setHours(0, 0, 0, 0);
      if (d < s) return false;
    }
    if (endDate) {
      const e = new Date(endDate);
      e.setHours(23, 59, 59, 999);
      if (d > e) return false;
    }
    if (selectedStatus && item.paymentStatus !== selectedStatus) return false;
    return true;
  });

  const filteredMonthlyByKey = filteredMonthlyItems.reduce((acc, item) => {
    const key = item.monthKey;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const availableFilteredMonths = Object.keys(filteredMonthlyByKey).sort().reverse();
  const itemsInSelectedMonth = filteredMonthlyItems;
  const fromMonthly = (currentPageMonthly - 1) * itemsPerPageMonthly;
  const paginatedMonthlyItems = itemsInSelectedMonth.slice(fromMonthly, fromMonthly + itemsPerPageMonthly);

  useEffect(() => {
    if (!selectedActionKey) return;
    const wrapper = tableScrollRef.current;
    if (!wrapper) return;
    const activeRow = wrapper.querySelector("tr.active-focus-row");
    if (!activeRow) return;
    activeRow.scrollIntoView({ block: "center", behavior: "auto" });
  }, [
    selectedActionKey,
    viewMode,
    currentPage,
    itemsPerPage,
    currentPageMonthly,
    itemsPerPageMonthly,
    paginated.length,
    paginatedMonthlyItems.length,
  ]);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon icon="mdi:unfold-more-horizontal" className="w-4 h-4 inline ml-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon icon="mdi:chevron-up" className="w-4 h-4 inline ml-1" />
      : <Icon icon="mdi:chevron-down" className="w-4 h-4 inline ml-1" />;
  };

  const formatDate = (d) =>
    d ? (formatDateUtil(d instanceof Date ? d.toISOString() : d) || "-") : "-";

  const updateReviewStatus = async (item, reviewStatus) => {
    // Optimistic update
    setContracts((prev) =>
      prev.map((c) => ({
        ...c,
        allInvoices: (c.allInvoices || []).map((inv) =>
          inv.CT_Pay_Id === item.CT_Pay_Id ? { ...inv, Review_Status: reviewStatus } : inv
        ),
      }))
    );
    try {
      await api.put(`/update-contract-invoice/${item.CT_Pay_Id}`, { Review_Status: reviewStatus });
    } catch (err) {
      console.error("Error updating review status:", err);
      // Rollback
      setContracts((prev) =>
        prev.map((c) => ({
          ...c,
          allInvoices: (c.allInvoices || []).map((inv) =>
            inv.CT_Pay_Id === item.CT_Pay_Id ? { ...inv, Review_Status: item.Review_Status } : inv
          ),
        }))
      );
      Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: "ไม่สามารถบันทึกสถานะการตรวจสอบได้" });
    }
  };

  const openNativeDatePicker = (inputRef) => {
    const input = inputRef?.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      
      <main
        className="flex-1 h-screen overflow-auto bg-gray-50 transition-all duration-300"
        style={{ marginLeft: "var(--sidebar-width)" }}
      >
        <Topbar
          breadcrumbs={[
            { label: "งานบัญชี", link: "/invoice-record" },
            { label: "บันทึกการชำระเงิน" },
          ]}
        />

        <div className="pl-2 pr-8 pb-4 pt-16" style={{ height: "calc(100vh - 64px)", display: "flex", flexDirection: "column" }}>
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]"></div>
              <p className="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          ) : (
            <div
              className="card shadow-sm"
              style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
            >
              <style>{`
                * { font-size: 14px !important; }

                .table-wrapper-scroll { 
                  padding: 0 0 0 20px !important; 
                }

                table.table {
                    border-collapse: separate !important;
                    border-spacing: 1px !important;
                    width: 100%;
                    min-width: 1200px;
                }

                th, td {
                    padding: 8px 10px !important;
                    background: white !important;
                    text-align: center;
                    outline: 1px solid #d0d0d0;
                    outline-offset: -1px;
                    white-space: nowrap;
                }

                /* --- HEADER STYLE --- */
                thead th {
                    background-color: #E13F57 !important;
                    color: white !important;
                    font-weight: 600;
                    text-align: center;
                    z-index: 10;
                }

                thead th.sortable {
                    cursor: pointer;
                    user-select: none;
                    position: relative;
                    padding-right: 25px !important;
                }

                thead th.sortable:hover {
                    background-color: #c9354d !important;
                }

                td.sticky-action, th.sticky-action {
                    position: sticky;
                    right: 0;
                    background: white !important;
                    z-index: 20;
                    isolation: isolate;
                    box-shadow: -3px 0 5px rgba(0,0,0,0.12);
                    padding: 8px 12px 8px 10px !important;
                }

                tbody td.sticky-action {
                    background: white !important;
                }

                tbody tr:hover td.sticky-action {
                    background: #f5f7ff !important;
                }

                tbody tr.active-focus-row td {
                    background: #fdf2f8 !important;
                }

                tbody tr.active-focus-row:hover td {
                    background: #fdf2f8 !important;
                }

                thead th.sticky-action {
                    background-color: #E13F57 !important;
                    z-index: 21 !important;
                }

                .monthly-filter-datepicker .rdtPicker {
                    z-index: 9999 !important;
                }
              `}</style>

              <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom gap-2 overflow-x-auto">
                <div className="d-flex align-items-center gap-3 flex-nowrap">
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setViewMode("contract")}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        viewMode === "contract"
                          ? "bg-[#E13F57] text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      <Icon icon="mdi:file-document-multiple" className="w-4 h-4 inline mr-1" />
                      รายการตามสัญญา
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("monthly")}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        viewMode === "monthly"
                          ? "bg-[#E13F57] text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      <Icon icon="mdi:calendar-month" className="w-4 h-4 inline mr-1" />
                      รายการตามเดือน
                    </button>
                  </div>
                  <h5 className="m-0 fw-bold">
                    {viewMode === "contract"
                      ? "รายการสัญญาที่มีใบแจ้งหนี้ค้างชำระ"
                      : "รายการตามเดือน"}
                  </h5>

                  {viewMode === "contract" && (
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="ค้นหา..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="h-[36px] w-[250px] border border-gray-300 rounded-md px-3 pr-8 py-1 text-sm"
                      />
                      {search !== "" && (
                        <button
                          onClick={() => {
                            setSearch("");
                            setCurrentPage(1);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <Icon icon="mdi:close" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {viewMode === "monthly" && (
                    <div className="d-flex align-items-center gap-1 flex-nowrap monthly-filter-datepicker">
                      <label className="text-sm fw-medium mb-0 mr-1" style={{ width: "25px" }}>ปี:</label>
                      <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        className="h-[36px] border border-gray-300 rounded-md px-2 py-1 text-sm min-w-[110px] mr-2"
                      >
                        <option value="">ทั้งหมด</option>
                        {yearOptions.map((y) => (
                          <option key={y} value={y}>
                            {y + 543}
                          </option>
                        ))}
                      </select>

                      <label className="text-sm fw-medium mb-0 mr-1" style={{ width: "70px" }}>วันที่เริ่มต้น:</label>
                      <div className="relative mr-2">
                        <input
                          type="text"
                          readOnly
                          value={formatIsoToBE(startDate) === "-" ? "" : formatIsoToBE(startDate)}
                          placeholder="วว/ดด/ปปปป"
                          className="h-[36px] w-[150px] border border-gray-300 rounded-md pl-2 pr-14 py-1 text-sm bg-white"
                        />
                        {startDate && (
                          <button
                            type="button"
                            aria-label="ล้างวันที่เริ่มต้น"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStartDate("");
                            }}
                            className="absolute right-7 top-1/2 -translate-y-1/2 z-20 text-gray-500 hover:text-gray-700"
                          >
                            <Icon icon="mdi:close-circle" className="w-4 h-4" />
                          </button>
                        )}
                        <Icon
                          icon="mdi:calendar-month-outline"
                          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4"
                        />
                        <input
                          type="date"
                          ref={startDateInputRef}
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="absolute inset-0 h-[36px] w-[150px] opacity-0 pointer-events-none"
                        />
                        <button
                          type="button"
                          aria-label="เลือกวันที่เริ่มต้น"
                          onClick={() => openNativeDatePicker(startDateInputRef)}
                          className="absolute inset-0 h-[36px] w-[150px] bg-transparent z-10"
                        />
                      </div>

                      <label className="text-sm fw-medium mb-0 mr-1" style={{ width: "70px" }}>วันที่สิ้นสุด:</label>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={formatIsoToBE(endDate) === "-" ? "" : formatIsoToBE(endDate)}
                          placeholder="วว/ดด/ปปปป"
                          className="h-[36px] w-[150px] border border-gray-300 rounded-md pl-2 pr-14 py-1 text-sm bg-white"
                        />
                        {endDate && (
                          <button
                            type="button"
                            aria-label="ล้างวันที่สิ้นสุด"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEndDate("");
                            }}
                            className="absolute right-7 top-1/2 -translate-y-1/2 z-20 text-gray-500 hover:text-gray-700"
                          >
                            <Icon icon="mdi:close-circle" className="w-4 h-4" />
                          </button>
                        )}
                        <Icon
                          icon="mdi:calendar-month-outline"
                          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4"
                        />
                        <input
                          type="date"
                          ref={endDateInputRef}
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="absolute inset-0 h-[36px] w-[150px] opacity-0 pointer-events-none"
                        />
                        <button
                          type="button"
                          aria-label="เลือกวันที่สิ้นสุด"
                          onClick={() => openNativeDatePicker(endDateInputRef)}
                          className="absolute inset-0 h-[36px] w-[150px] bg-transparent z-10"
                        />
                      </div>

                      <label className="text-sm fw-medium mb-0 ml-2 mr-1">สถานะ:</label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="h-[36px] border border-gray-300 rounded-md px-2 py-1 text-sm min-w-[130px]"
                      >
                        <option value="">ทั้งหมด</option>
                        <option value="pending">รอชำระ</option>
                        <option value="partial">จ่ายบางส่วน</option>
                        <option value="overdue">ค้างชำระ</option>
                        <option value="paid">จ่ายแล้ว</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* TABLE */}
              <div
                ref={tableScrollRef}
                className="flex-1 table-wrapper-scroll"
                style={{ flex: 1, overflowX: "auto", overflowY: "auto" }}
              >
                {viewMode === "contract" ? (
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th 
                          className="sortable"
                          onClick={() => handleSort('Doc_No')}
                        >
                          เลขที่สัญญา {getSortIcon('Doc_No')}
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort('DisplayName')}
                        >
                          ชื่อลูกค้า {getSortIcon('DisplayName')}
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort('unpaidCount')}
                        >
                          จำนวนใบแจ้งหนี้ที่ยังไม่ชำระ {getSortIcon('unpaidCount')}
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort('totalUnpaidAmount')}
                        >
                          ยอดค้างชำระรวม {getSortIcon('totalUnpaidAmount')}
                        </th>
                        <th 
                          className="sortable"
                          onClick={() => handleSort('Created_Date')}
                        >
                          วันที่สร้างสัญญา {getSortIcon('Created_Date')}
                        </th>
                        <th ref={actionRef} className="sticky-action">
                          การจัดการ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length > 0 ? (
                        paginated.map((contract) => {
                          const focusKey = `contract:${contract.Doc_Id}`;
                          return (
                          <tr key={contract.Doc_Id} className={selectedActionKey === focusKey ? "active-focus-row" : ""}>
                            <td className="font-medium text-gray-900">
                              {contract.Doc_No ? (
                                <Link
                                  to={`/contract/edit/${contract.Doc_Id}`}
                                  state={{
                                    fromInvoiceRecord: true,
                                    invoiceRecordBackLink: buildInvoiceRecordBackLink(focusKey),
                                    invoiceRecordBackLabel: "บันทึกการชำระเงิน",
                                  }}
                                  onClick={() => setSelectedActionKey(focusKey)}
                                  className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {contract.Doc_No}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="text-left">
                              {contract.DisplayName || 
                               `${contract.AR_Name || ""} ${contract.AR_LastName || ""}`.trim() || 
                               "-"}
                            </td>
                            <td>
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                {contract.unpaidCount} ใบ
                              </span>
                            </td>
                            <td className="text-right">
                              <span className="font-semibold text-red-600">
                                {Number(contract.totalUnpaidAmount || 0).toLocaleString()}
                              </span>
                            </td>
                            <td>{formatDate(contract.Created_Date)}</td>
                            <td className="sticky-action">
                              <div className="d-flex gap-2 justify-center">
                                <button
                                  onClick={() => {
                                    setSelectedActionKey(focusKey);
                                    navigate(`/jobfile/${contract.Doc_Id}/invoice-record`, {
                                      state: {
                                        backLink: buildInvoiceRecordBackLink(focusKey),
                                        backLabel: "บันทึกการชำระเงิน",
                                      },
                                    });
                                  }}
                                  className="btn btn-sm btn-warning text-white px-3 py-1"
                                  style={{ 
                                    backgroundColor: '#E13F57',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    minWidth: '120px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px'
                                  }}
                                  title="บันทึกการชำระเงิน"
                                >
                                  <Icon icon="mdi:file-document-edit" width={16} />
                                  บันทึกการชำระเงิน
                                </button>
                              </div>
                            </td>
                          </tr>
                        )})
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center py-4 text-gray-500">
                            {search
                              ? "ไม่พบสัญญาที่ค้นหา"
                              : "ไม่มีสัญญาที่มีใบแจ้งหนี้ที่ยังไม่ชำระ"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>เลขที่สัญญา</th>
                        <th>ชื่อลูกค้า</th>
                        <th>งวดที่</th>
                        <th>เลขที่ใบแจ้งหนี้</th>
                        <th>วันที่ครบกำหนดชำระ</th>
                        <th>สถานะการชำระ</th>
                        <th>ยอดที่ชำระมา</th>
                        <th>ยอดค้างชำระ</th>
                        <th>การตรวจสอบ</th>
                        <th ref={actionRef} className="sticky-action">
                          การจัดการ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedMonthlyItems.length > 0 ? (
                        paginatedMonthlyItems.map((item, i) => {
                          const focusKey = `invoice:${item.CT_Pay_Id}`;
                          return (
                          <tr key={`${item.Doc_Id}-${item.CT_Pay_Id}-${fromMonthly + i}`} className={selectedActionKey === focusKey ? "active-focus-row" : ""}>
                            <td className="font-medium text-gray-900">
                              {item.Doc_No ? (
                                <Link
                                  to={`/contract/edit/${item.Doc_Id}`}
                                  state={{
                                    fromInvoiceRecord: true,
                                    invoiceRecordBackLink: buildInvoiceRecordBackLink(focusKey),
                                    invoiceRecordBackLabel: "บันทึกการชำระเงิน",
                                  }}
                                  onClick={() => setSelectedActionKey(focusKey)}
                                  className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {item.Doc_No}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="text-left">{item.DisplayName || "-"}</td>
                            <td>งวดที่ {item.Line ?? "-"}</td>
                            <td>{item.Invoice_No || "-"}</td>
                            <td>
                              {item.paymentStatus === "overdue" ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  {formatDate(item.dueDate)}
                                </span>
                              ) : (
                                formatDate(item.dueDate)
                              )}
                            </td>
                            <td>
                              {item.paymentStatus === "paid" ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  จ่ายแล้ว
                                </span>
                              ) : item.paymentStatus === "partial" ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  จ่ายบางส่วน
                                </span>
                              ) : item.paymentStatus === "overdue" ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  ค้างชำระ
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  รอชำระ
                                </span>
                              )}
                            </td>
                            <td className="text-right">
                              <span className="font-medium text-green-700">
                                {Number(item.paidAmount || 0).toLocaleString()}
                              </span>
                            </td>
                            <td className="text-right">
                              <span className="font-semibold text-red-600">
                                {Number(item.remainingAmount || 0).toLocaleString()}
                              </span>
                            </td>
                            <td>
                              {(item.paymentStatus === "paid" || item.paymentStatus === "partial") ? (
                                <select
                                  value={item.Review_Status ?? 0}
                                  onChange={(e) => updateReviewStatus(item, parseInt(e.target.value))}
                                  className={`h-[30px] border rounded-md px-2 text-xs font-medium ${
                                    item.Review_Status === 1
                                      ? "border-green-400 bg-green-50 text-green-800"
                                      : "border-yellow-400 bg-yellow-50 text-yellow-800"
                                  }`}
                                >
                                  <option value={0}>รอตรวจสอบ</option>
                                  <option value={1}>ตรวจสอบแล้ว</option>
                                </select>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="sticky-action">
                              <div className="d-flex gap-2 justify-center">
                                <button
                                  onClick={() => {
                                    setSelectedActionKey(focusKey);
                                    navigate(`/jobfile/${item.Doc_Id}/invoice-record`, {
                                      state: {
                                        backLink: buildInvoiceRecordBackLink(focusKey),
                                        backLabel: "บันทึกการชำระเงิน",
                                      },
                                    });
                                  }}
                                  className="btn btn-sm text-white px-3 py-1"
                                  style={{ 
                                    backgroundColor: '#E13F57',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    minWidth: '120px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px'
                                  }}
                                  title="บันทึกการชำระเงิน"
                                >
                                  <Icon icon="mdi:file-document-edit" width={16} />
                                  บันทึกการชำระเงิน
                                </button>
                              </div>
                            </td>
                          </tr>
                        )})
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center py-4 text-gray-500">
                            {itemsInSelectedMonth.length === 0
                              ? "ไม่มีใบแจ้งหนี้ค้างชำระที่มีวันที่ครบกำหนด"
                              : "ไม่มีรายการเก็บหนี้ตามเงื่อนไขที่เลือก"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {/* PAGINATION */}
              {viewMode === "contract" ? (
                <Pagination
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onItemsPerPageChange={(val) => {
                    setItemsPerPage(val);
                    setCurrentPage(1);
                  }}
                  onPageChange={setCurrentPage}
                />
              ) : (
                <div className="border-top">
                  <div className="px-4 py-1 text-sm text-gray-600">
                    ยอดค้างชำระรวมตามเงื่อนไข:{" "}
                    <span className="font-semibold text-red-600">
                      {itemsInSelectedMonth
                        .reduce((s, i) => s + (i.remainingAmount || 0), 0)
                        .toLocaleString()}
                    </span>
                  </div>
                  <Pagination
                    totalItems={itemsInSelectedMonth.length}
                    itemsPerPage={itemsPerPageMonthly}
                    currentPage={currentPageMonthly}
                    onItemsPerPageChange={(val) => {
                      setItemsPerPageMonthly(val);
                      setCurrentPageMonthly(1);
                    }}
                    onPageChange={setCurrentPageMonthly}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

